// Pure projection math for the Locations globe. Same convention as the home globe — a Y-axis
// rotation (`rot`) — plus an optional X-axis `tilt` so a focused country can be centered
// vertically as well as horizontally. When tilt = 0 this reduces EXACTLY to the home-globe
// formula, so explore mode looks identical to the existing globe.

import type { UPoint } from "@/lib/globe-borders";
import type { Camera } from "./types";

const DEG = Math.PI / 180;

export interface Projected {
  x: number;
  y: number;
  /** Depth along the view axis; > 0 is the front (visible) hemisphere. */
  depth: number;
}

export function makeCamera(cx: number, cy: number, rG: number, rot: number, tilt: number): Camera {
  return {
    cx,
    cy,
    rG,
    cr: Math.cos(rot),
    sr: Math.sin(rot),
    ct: Math.cos(tilt),
    st: Math.sin(tilt),
  };
}

/** Project a unit-sphere point to screen coords (Y-rotation, then X-tilt).
 *  Screen X is negated vs the raw sphere x so longitude reads correctly — east to the right,
 *  west to the left (the lon/lat→unit convention is otherwise east-west mirrored on screen). */
export function project(p: UPoint, cam: Camera): Projected {
  const x1 = p.ux * cam.cr - p.uz * cam.sr;
  const y1 = p.uy;
  const z1 = p.ux * cam.sr + p.uz * cam.cr;
  const yp = y1 * cam.ct - z1 * cam.st;
  const zp = y1 * cam.st + z1 * cam.ct;
  return { x: cam.cx - cam.rG * x1, y: cam.cy - cam.rG * yp, depth: zp };
}

/** Inverse of `project` for a screen point: returns lon/lat (degrees) on the FRONT hemisphere, or null if outside the disc. */
export function invert(sx: number, sy: number, cam: Camera): { lon: number; lat: number } | null {
  // X is negated to match the mirror-corrected `project` above.
  const rx = -(sx - cam.cx) / cam.rG;
  const ry = -(sy - cam.cy) / cam.rG;
  const d2 = rx * rx + ry * ry;
  if (d2 > 1) return null;
  const rz = Math.sqrt(1 - d2);
  // Undo tilt (rotate by -tilt around X), then undo Y rotation.
  const y1 = ry * cam.ct + rz * cam.st;
  const z1 = -ry * cam.st + rz * cam.ct;
  const x1 = rx;
  const ux = x1 * cam.cr + z1 * cam.sr;
  const uz = -x1 * cam.sr + z1 * cam.cr;
  const uy = y1;
  const lat = Math.asin(Math.max(-1, Math.min(1, uy))) / DEG;
  const lon = Math.atan2(uz, ux) / DEG;
  return { lon, lat };
}

/** Y-rotation that brings a given longitude to front-center (same convention as the home globe). */
export function rotForLon(lonDeg: number): number {
  const lng = ((lonDeg + 360) % 360) * DEG;
  return Math.PI / 2 - lng;
}

/** X-tilt that brings a given latitude to front-center, clamped so a pole never swings to the front. */
export function tiltForLat(latDeg: number, maxDeg = 55): number {
  const clamped = Math.max(-maxDeg, Math.min(maxDeg, latDeg));
  return clamped * DEG;
}
